// Hook: subscribes to SSE + polls as fallback to detect when an order is paid via QR.
import { useEffect, useRef, useState, useCallback } from "react";
import { openSSE } from "@/lib/sse/event-source-client";
import { apiGet } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/store";

export type PaymentListenerStatus = "idle" | "listening" | "paid" | "cancelled" | "disconnected";

interface PaymentStatus {
  status: string;
  paidAt?: string | null;
  amount?: number;
  expiresAt?: string | null;
  orderStatus?: string;
}

interface UseOrderPaymentSSEResult {
  status: PaymentListenerStatus;
  paidAt: string | null;
  refetch: () => void;
}

const POLL_INTERVAL_MS = 3000;

/**
 * Listens for order payment confirmation via SSE with a 3s polling fallback.
 * Whichever source reports "paid" first wins.
 * @param orderID - UUID of the order to watch (null = inactive)
 */
export function useOrderPaymentSSE(orderID: string | null): UseOrderPaymentSSEResult {
  const [status, setStatus] = useState<PaymentListenerStatus>("idle");
  const [paidAt, setPaidAt] = useState<string | null>(null);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Ref to detect if component is still mounted and avoid stale closures.
  const paidRef = useRef(false);

  const markPaid = useCallback((at: string | null) => {
    if (paidRef.current) return;
    paidRef.current = true;
    setPaidAt(at ?? new Date().toISOString());
    setStatus("paid");
  }, []);

  const markCancelled = useCallback(() => {
    if (paidRef.current) return; // already resolved
    setStatus("cancelled");
  }, []);

  const refetch = useCallback(async () => {
    if (!orderID || paidRef.current) return;
    try {
      const data = await apiGet<PaymentStatus>(`/v1/orders/${orderID}/payment-status`);
      if (data.status === "paid" || data.orderStatus === "paid") {
        markPaid(data.paidAt ?? null);
      } else if (data.status === "cancelled") {
        markCancelled();
      }
    } catch {
      // Polling errors are non-fatal.
    }
  }, [orderID, markPaid, markCancelled]);

  useEffect(() => {
    if (!orderID || !accessToken) {
      setStatus("idle");
      paidRef.current = false;
      return;
    }

    paidRef.current = false;
    setStatus("listening");

    // SSE subscription.
    const sseClient = openSSE({
      token: accessToken,
      topic: `order.paid.${orderID}`,
      onMessage: (data) => {
        const d = data as { paidAt?: string };
        markPaid(d?.paidAt ?? null);
      },
      onError: () => {
        if (!paidRef.current) setStatus("disconnected");
      },
    });

    // Polling fallback: every 3s regardless of SSE health.
    const pollId = setInterval(refetch, POLL_INTERVAL_MS);

    return () => {
      sseClient.close();
      clearInterval(pollId);
    };
  }, [orderID, accessToken, markPaid, refetch]);

  return { status, paidAt, refetch };
}
